import { LOGO_URL, APP_NAME, COMPANY_NAME, CONTACT_EMAIL, CONTACT_PHONE } from '../lib/constants';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsAndConditions = () => {
  const lastUpdated = "January 3, 2025";
  
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt={APP_NAME} className="h-8" />
            <span className="font-bold text-[#0F172A] font-['Manrope']">{APP_NAME}</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#64748B]">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-2 font-['Manrope']">
            Terms and Conditions
          </h1>
          <p className="text-[#64748B] mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">1. Introduction</h2>
              <p className="text-[#475569] mb-4">
                Welcome to {APP_NAME} ("{APP_NAME}", "we", "us", or "our"), a service provided by {COMPANY_NAME}. 
                These Terms and Conditions ("Terms") govern your access to and use of our website, applications, 
                and compliance assessment services (collectively, the "Services").
              </p>
              <p className="text-[#475569] mb-4">
                By accessing or using our Services, you agree to be bound by these Terms. If you do not agree 
                to these Terms, please do not use our Services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">2. Description of Services</h2>
              <p className="text-[#475569] mb-4">
                {APP_NAME} provides data protection compliance assessment tools for businesses operating in or 
                targeting customers in Caribbean jurisdictions, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Jamaica (Data Protection Act, 2020)</li>
                <li>Barbados (Data Protection Act, 2019)</li>
                <li>Bermuda (Personal Information Protection Act, 2016)</li>
                <li>Cayman Islands (Data Protection Act, 2017)</li>
              </ul>
              <p className="text-[#475569] mb-4">
                Our Services include self-assessment questionnaires, compliance scoring, action plan generation, 
                and PDF report generation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">3. Important Disclaimer</h2>
              <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 rounded-r-lg mb-4">
                <p className="text-[#92400E] font-medium">
                  THE SERVICES PROVIDED BY {APP_NAME.toUpperCase()} ARE FOR INFORMATIONAL AND EDUCATIONAL 
                  PURPOSES ONLY AND DO NOT CONSTITUTE LEGAL ADVICE.
                </p>
              </div>
              <p className="text-[#475569] mb-4">
                The assessments, scores, recommendations, and reports generated by our Services are intended 
                to provide general guidance on data protection compliance. They should not be relied upon as 
                a substitute for professional legal advice from a qualified attorney or data protection specialist.
              </p>
              <p className="text-[#475569] mb-4">
                We strongly recommend that you consult with a qualified legal professional before making any 
                decisions based on the results of our assessments, particularly for complex compliance matters 
                or when facing regulatory action.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">4. User Accounts</h2>
              <p className="text-[#475569] mb-4">
                To access certain features of our Services, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Accept responsibility for all activities that occur under your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">5. Acceptable Use</h2>
              <p className="text-[#475569] mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Use the Services for any unlawful purpose or in violation of any applicable laws</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Interfere with or disrupt the integrity or performance of the Services</li>
                <li>Share your account credentials with third parties</li>
                <li>Use automated systems (bots, scrapers) to access the Services without permission</li>
                <li>Reproduce, duplicate, copy, sell, or resell any portion of the Services</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">6. Intellectual Property</h2>
              <p className="text-[#475569] mb-4">
                All content, features, and functionality of the Services, including but not limited to text, 
                graphics, logos, icons, images, audio clips, digital downloads, data compilations, software, 
                and the compilation thereof, are the exclusive property of {COMPANY_NAME} or its licensors 
                and are protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p className="text-[#475569] mb-4">
                You are granted a limited, non-exclusive, non-transferable license to access and use the 
                Services for your internal business purposes only.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">7. Limitation of Liability</h2>
              <p className="text-[#475569] mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, {COMPANY_NAME.toUpperCase()} AND ITS 
                OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Any loss of profits, revenue, data, or business opportunities</li>
                <li>Any damages arising from your reliance on the assessment results</li>
                <li>Any regulatory fines or penalties imposed on your organization</li>
                <li>Any damages resulting from unauthorized access to your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">8. Indemnification</h2>
              <p className="text-[#475569] mb-4">
                You agree to indemnify, defend, and hold harmless {COMPANY_NAME}, its officers, directors, 
                employees, agents, and affiliates from and against any claims, liabilities, damages, losses, 
                and expenses, including reasonable attorneys' fees, arising out of or in any way connected 
                with your access to or use of the Services or your violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">9. Modifications to Terms</h2>
              <p className="text-[#475569] mb-4">
                We reserve the right to modify these Terms at any time. We will notify you of any material 
                changes by posting the new Terms on this page and updating the "Last Updated" date. Your 
                continued use of the Services after such modifications constitutes your acceptance of the 
                revised Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">10. Termination</h2>
              <p className="text-[#475569] mb-4">
                We may terminate or suspend your account and access to the Services immediately, without 
                prior notice or liability, for any reason, including if you breach these Terms. Upon 
                termination, your right to use the Services will immediately cease.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">11. Governing Law</h2>
              <p className="text-[#475569] mb-4">
                These Terms shall be governed by and construed in accordance with the laws of Trinidad and 
                Tobago, without regard to its conflict of law provisions. Any disputes arising from these 
                Terms or your use of the Services shall be subject to the exclusive jurisdiction of the 
                courts of Trinidad and Tobago.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-[#0F172A] mb-4">12. Contact Information</h2>
              <p className="text-[#475569] mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-[#0F172A] font-semibold mb-2">{COMPANY_NAME}</p>
                <p className="text-[#475569] flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#3B82F6] hover:underline">{CONTACT_EMAIL}</a>
                </p>
                <p className="text-[#475569] flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:DPO@myprivicy.com" className="text-[#3B82F6] hover:underline">DPO@myprivicy.com</a> (Data Protection Officer)
                </p>
                <p className="text-[#475569] flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {CONTACT_PHONE}
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-[#64748B]">
          <Link to="/privacy" className="hover:text-[#3B82F6]">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-[#3B82F6]">Cookie Policy</Link>
          <Link to="/" className="hover:text-[#3B82F6]">Back to Home</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;
